import type {
  PhotowallArrivalDirection,
  PhotowallArrivalStyle,
} from "./usePhotowallArrivals";

export type PhotowallArrivalEdge = "left" | "right" | "top" | "bottom";

interface ArrivalMotionOptions {
  style: Exclude<PhotowallArrivalStyle, "off">;
  direction: PhotowallArrivalDirection;
  edge: PhotowallArrivalEdge;
  stageWidth: number;
  stageHeight: number;
  /** Distance from the QR code's side of the stage that cards must stay out of. */
  qrExtent: number;
  baseCardWidth: number;
  /** 0 for a lone photo, 1 for a burst of uploads. */
  energy: number;
  seed: number;
  isReducedMotion: boolean;
}

export interface ArrivalMotion {
  width: number;
  zIndex: number;
  duration: number;
  keyframes: Keyframe[];
  easing: string;
}

// Base flight times before depth, random spread and screen size. A burst
// moves quicker than a lone photo so more fit through.
const CALM_DURATION_MS = 7500;
const BURST_DURATION_MS = 4500;
const REDUCED_CALM_DURATION_MS = 4500;
const REDUCED_BURST_DURATION_MS = 2800;
// Dense enough that sampled sway and wobble read as smooth curves.
const SAMPLE_INTERVAL_MS = 80;
// Tilted cards are taller than their box, so they wait further off screen.
const OFFSCREEN_MARGIN = 24;
const OFFSCREEN_TILT_ALLOWANCE = 0.4;
const QR_GAP = 16;
// How far past its box a tilted card reaches, as a share of its length.
const POLAROID_TILT_REACH = 0.2;
const BUBBLE_TILT_REACH = 0.04;
const MIN_CARD_WIDTH = 40;

function lerp(from: number, to: number, amount: number): number {
  return from + (to - from) * amount;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function smoothstep(from: number, to: number, value: number): number {
  const amount = clamp((value - from) / (to - from), 0, 1);
  return amount * amount * (3 - 2 * amount);
}

// Seeded so a card keeps its path if React renders it again.
function createRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Builds one card's flight: tossed in from off screen, slowing as it settles
 * into a drifting sway, then easing away. Calm cards linger near the middle;
 * cards in a burst fan out across a wider lane at varied depths.
 */
export function buildArrivalMotion({
  style,
  direction,
  edge,
  stageWidth,
  stageHeight,
  qrExtent,
  baseCardWidth,
  energy: rawEnergy,
  seed,
  isReducedMotion,
}: ArrivalMotionOptions): ArrivalMotion {
  const energy = clamp(rawEnergy, 0, 1);
  const random = createRandom(seed);
  const range = (min: number, max: number): number => lerp(min, max, random());
  const sign = (): number => (random() < 0.5 ? -1 : 1);

  if (isReducedMotion) {
    return {
      width: baseCardWidth,
      zIndex: 1,
      duration: lerp(REDUCED_CALM_DURATION_MS, REDUCED_BURST_DURATION_MS, energy),
      keyframes: [
        { opacity: 0, offset: 0 },
        { opacity: 1, offset: 0.08 },
        { opacity: 1, offset: 0.92 },
        { opacity: 0, offset: 1 },
      ],
      easing: "ease",
    };
  }

  const isVertical = direction === "bottom-to-top" || direction === "top-to-bottom";
  const isBubble = style === "bubble";

  const mainSize = isVertical ? stageHeight : stageWidth;
  const crossSize = isVertical ? stageWidth : stageHeight;
  const aspect = isBubble ? 1 : 1.22;
  const tiltReach = isBubble ? BUBBLE_TILT_REACH : POLAROID_TILT_REACH;
  const inset = Math.min(32, stageWidth * 0.04);
  // Room beside the QR code, which can be tight on a short landscape phone.
  const qrRoom = qrExtent > 0 ? crossSize - qrExtent - QR_GAP - inset : Infinity;

  // Lone photos come in large and close; bursts spread over near and far cards.
  // Cards shrink when the QR code leaves too little room for their tilt.
  const depth = lerp(range(0.88, 1), range(0.05, 1), energy);
  const fitWidth = qrRoom / (isVertical ? 1 + aspect * tiltReach : aspect + tiltReach);
  const width = Math.max(
    MIN_CARD_WIDTH,
    Math.min(baseCardWidth * lerp(0.65, 1, depth), fitWidth)
  );
  const height = width * aspect;
  const cardMain = isVertical ? height : width;
  const cardCross = isVertical ? width : height;

  const offscreen = OFFSCREEN_MARGIN + cardMain * OFFSCREEN_TILT_ALLOWANCE;
  const travel = mainSize + cardMain + offscreen * 2;
  // Far cards drift a little slower, and burst speeds vary more so a flurry
  // strings out into a stream instead of moving as one clump.
  const speedSpread = lerp(0.08, 0.22, energy);
  const duration =
    lerp(CALM_DURATION_MS, BURST_DURATION_MS, energy) *
    lerp(1.1, 0.92, depth) *
    range(1 - speedSpread, 1 + speedSpread) *
    clamp(Math.sqrt(travel / 1250), 0.75, 1.35);
  const seconds = duration / 1000;

  // The lane hugs the edge for lone photos and widens during a burst, but
  // never reaches the QR code.
  const bandLimit = Math.max(
    inset + cardCross,
    Math.min(
      crossSize * lerp(0.2, 0.36, energy),
      qrExtent > 0 ? crossSize - qrExtent - QR_GAP - cardMain * tiltReach : Infinity
    )
  );
  let swayAmplitude = cardCross * lerp(0.14, 0.2, energy) * (isBubble ? 1.5 : 1);
  const swayLimit = (bandLimit - inset - cardCross) / 2;
  swayAmplitude = clamp(swayAmplitude, 0, Math.max(0, swayLimit));
  const laneSpan = Math.max(0, bandLimit - inset - cardCross - swayAmplitude * 2);
  const laneSpread = lerp(0.3, 1, energy);
  const lane = swayAmplitude + random() * laneSpan * laneSpread;
  // Burst cards launch from one spot and fan out to their own lane.
  const launch = lerp(lane, swayAmplitude + laneSpan * 0.5, energy * range(0.6, 1));
  const toCross = (offset: number): number =>
    edge === "right" || edge === "bottom"
      ? crossSize - inset - offset - cardCross
      : inset + offset;

  const mainStart =
    direction === "bottom-to-top" || direction === "right-to-left"
      ? mainSize + offscreen
      : -cardMain - offscreen;
  const mainEnd =
    direction === "bottom-to-top" || direction === "right-to-left"
      ? -cardMain - offscreen
      : mainSize + offscreen;
  // Positive when travelling down or right, so a card leans into its drift.
  const leanSign =
    direction === "bottom-to-top" || direction === "left-to-right" ? 1 : -1;

  // Speed profile: a toss on entry, a slower stretch mid-screen, then a
  // gentle acceleration away. Integrated below into distance travelled.
  const toss = lerp(1.2, 2.2, energy) * (isBubble ? 0.45 : 1);
  const linger = lerp(0.25, 0.05, energy);
  const exit = lerp(0.4, 0.9, energy);
  const speedAt = (progress: number): number =>
    1 +
    toss * Math.exp(-progress / 0.07) -
    linger * Math.exp(-((progress - 0.45) ** 2) / (2 * 0.12 ** 2)) +
    exit * smoothstep(0.7, 1, progress);

  const swayFrequency = range(0.16, 0.24) * lerp(1, 1.3, energy) * (isBubble ? 1.2 : 1);
  const swayPhase = range(0, Math.PI * 2);
  const ripple = range(2.2, 2.8);
  const rippleShare = range(0.2, 0.35);
  const ripplePhase = range(0, Math.PI * 2);
  const baseTilt = isBubble ? 0 : sign() * range(2, 5.5);
  const tossTilt = isBubble ? 0 : sign() * range(6, 14) * lerp(0.6, 1.2, energy);
  const tumble = isBubble ? 0 : sign() * range(0, 28) * energy;
  const leanDegrees = isBubble ? 0 : lerp(2.5, 4, energy);
  const wobble = isBubble ? 0.03 : 0;
  const wobbleFrequency = range(0.8, 1.2);
  const wobblePhase = range(0, Math.PI * 2);

  const frameCount = Math.max(2, Math.ceil(duration / SAMPLE_INTERVAL_MS) + 1);
  const distances = [0];
  for (let index = 1; index < frameCount; index += 1) {
    const before = speedAt((index - 1) / (frameCount - 1));
    const after = speedAt(index / (frameCount - 1));
    distances.push(distances[index - 1] + (before + after) / 2);
  }
  const totalDistance = distances[frameCount - 1];

  const keyframes: Keyframe[] = distances.map((distance, index) => {
    const progress = index / (frameCount - 1);
    const time = progress * seconds;
    const settle = Math.exp(-progress / 0.09);
    const swayIn = 1 - Math.exp(-progress / 0.15);
    const angle = Math.PI * 2 * swayFrequency * time;
    const sway =
      swayAmplitude *
      swayIn *
      (Math.sin(angle + swayPhase) * (1 - rippleShare) +
        Math.sin(angle * ripple + ripplePhase) * rippleShare);
    const swayVelocity =
      Math.cos(angle + swayPhase) * (1 - rippleShare) +
      Math.cos(angle * ripple + ripplePhase) * rippleShare * ripple;
    const crossOffset = lerp(lane, launch, settle) + sway;
    const mainPosition = lerp(mainStart, mainEnd, distance / totalDistance);
    const cross = toCross(crossOffset);
    const x = isVertical ? cross : mainPosition;
    const y = isVertical ? mainPosition : cross;
    const rotation =
      baseTilt +
      tossTilt * settle +
      tumble * progress +
      leanSign * leanDegrees * swayIn * clamp(swayVelocity, -1, 1);
    const squash = wobble * Math.sin(Math.PI * 2 * wobbleFrequency * time + wobblePhase);

    return {
      offset: progress,
      transform:
        `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) ` +
        `rotate(${rotation.toFixed(2)}deg) ` +
        `scale(${(1 + squash).toFixed(4)}, ${(1 - squash).toFixed(4)})`,
    };
  });

  return {
    width,
    zIndex: 10 + Math.round(depth * 80),
    duration,
    keyframes,
    easing: "linear",
  };
}
